import React, { useEffect, useRef } from 'react';

interface Heart {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
}

const ValentineBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heartsRef = useRef<Heart[]>([]);
  const animationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create hearts
    const createHearts = () => {
      heartsRef.current = [];
      const heartCount = Math.floor((canvas.width * canvas.height) / 15000); // Density control
      
      for (let i = 0; i < heartCount; i++) {
        heartsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 20 + 15, // 15-35px
          speed: Math.random() * 0.5 + 0.3, // 0.3-0.8 pixels per frame (slow)
          opacity: Math.random() * 0.3 + 0.2 // 0.2-0.5 opacity
        });
      }
    };
    createHearts();

    // Draw heart shape
    const drawHeart = (x: number, y: number, size: number, opacity: number) => {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = '#FF1493'; // Deep pink color
      ctx.beginPath();
      
      // Start from the bottom point of the heart
      ctx.moveTo(x, y + size / 2);
      
      // Left side curve going up
      ctx.bezierCurveTo(
        x, y + size / 4,           // Control point 1
        x - size / 2, y,           // Control point 2
        x - size / 2, y - size / 4 // End point (left top)
      );
      
      // Left top bump (left lobe)
      ctx.bezierCurveTo(
        x - size / 2, y - size / 2, // Control point 1
        x - size / 4, y - size / 2, // Control point 2
        x, y - size / 4             // End point (center top)
      );
      
      // Right top bump (right lobe)
      ctx.bezierCurveTo(
        x + size / 4, y - size / 2, // Control point 1
        x + size / 2, y - size / 2, // Control point 2
        x + size / 2, y - size / 4  // End point (right top)
      );
      
      // Right side curve going down
      ctx.bezierCurveTo(
        x + size / 2, y,           // Control point 1
        x, y + size / 4,           // Control point 2
        x, y + size / 2            // End point (bottom point)
      );
      
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      heartsRef.current.forEach(heart => {
        drawHeart(heart.x, heart.y, heart.size, heart.opacity);
        
        // Move heart down slowly
        heart.y += heart.speed;
        
        // Reset heart to top when it goes off screen
        if (heart.y > canvas.height + heart.size) {
          heart.y = -heart.size;
          heart.x = Math.random() * canvas.width;
        }
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ background: 'linear-gradient(135deg, #FFB6C1, #FF69B4)' }}
    />
  );
};

export default ValentineBackground;
